// apps/web/src/features/admin/automations/rule-identify.ts
import { EFFECTS, type AuthoringStep } from '@capture/shared';

export interface IdentifiedRule {
  effectKey: string;
  label: string;
  bucket: 'check' | 'route';
  step: AuthoringStep;
}

/** Check + Route effects that can parse a persisted RuleAttachment, in registry order. */
const RULE_EFFECTS = EFFECTS.filter(
  (e) => (e.bucket === 'check' || e.bucket === 'route') && typeof e.parse === 'function',
);

/**
 * Identify a persisted RuleAttachment as one of the Check/Route effects by asking
 * each effect's registry `parse` to claim it. Check parses key off `rule.type`,
 * Route parses off `actions[0].type` — disjoint, so first-match is unambiguous.
 * Returns null for retired/unknown types (custom-script, dead field-engine actions).
 */
export function identifyRuleStep(rule: unknown): IdentifiedRule | null {
  for (const e of RULE_EFFECTS) {
    const step = e.parse!(rule);
    if (step) return { effectKey: e.key, label: e.label, bucket: e.bucket as 'check' | 'route', step };
  }
  return null;
}

function s(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/** One-line, defensive human summary of a compiled Check/Route step for the list. */
export function summarizeRuleStep(effectKey: string, config: Record<string, unknown>): string {
  switch (effectKey) {
    case 'validate_check': return s(config.type) ?? '(validation)';
    case 'cross_field_check': return s(config.name) ?? '(cross-field rule)';
    case 'unique_check': {
      const base = s(config.scope) ? `unique per ${config.scope}` : 'unique';
      const members = Array.isArray(config.fields) ? (config.fields as string[]).filter(Boolean) : [];
      const on = members.length > 0 ? ` on ${members.join(' + ')}` : '';
      const days = typeof config.withinDays === 'number' && config.withinDays > 0 ? ` within ${config.withinDays} days` : '';
      return `${base}${on}${days}`;
    }
    case 'historical_range_check': return 'within historical range';
    case 'webhook_check': return s(config.url) ?? '(external check)';
    case 'route_to_queue': return s(config.targetStep) ? `→ ${config.targetStep}` : '(route)';
    case 'hold': return 'hold';
    case 'escalate': return 'escalate';
    case 'reject': return 'reject';
    case 'skip_step': return s(config.targetStep) ? `skip ${config.targetStep}` : 'skip next step';
    case 'reclassify': return s(config.targetClassification) ? `reclassify → ${config.targetClassification}` : 'reclassify';
    case 'reprioritize': return Number.isFinite(config.priority) ? `priority → ${config.priority}` : '(change priority)';
    case 'notify': return '(notify)';
    case 'set_field': {
      const f = s(config.targetField);
      if (!f) return '(stamp value)';
      const v = JSON.stringify(String(config.value ?? ''));
      return config.mode === 'append' ? `add ${v} to ${f}` : `set ${f} to ${v}`;
    }
    default: return '';
  }
}
